import { PORTFOLIO_TYPE_CONSTS } from '@modules/edit-preference/components/portfolio/components/add-edit-portfolio-type/add-edit-portfolio-type.fields';
import { Application } from '@enums/application.enums';
import { PORTFOLIO_CONSTS } from '@constants/constant';
import { environment } from '@environments/environment';
import { DownloadService } from '@modules/shared/services/download-file.service';
import {
  PORTFOLIO_DETAIL_ROUTE_PATH_MAP,
  PORTFOLIO_INDIVIDUAL_SHARE,
  PORTFOLIO_MASTER_ROUTE_PATH_MAP,
  PORTFOLIO_MASTER_SHARE
} from '@modules/edit-preference/components/portfolio/components/portfolio-banner-view/portfolio-banner-view.constants';
import { SHARED_DIALOG_CONSTS } from '@shared/components/shareddialog/shareddialog.constants';

export const getRestrictLabel = (
  portfolioType: string,
  labelMap: Record<string, string>
): string => {
  const normalizedType =
    (portfolioType || '').toUpperCase() ||
    PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.CUSTOM;
  const type =
    normalizedType in labelMap
      ? normalizedType
      : PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.CUSTOM;

  return (
    labelMap[type] || labelMap[PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.CUSTOM]
  );
};

export const getSafeTitle = (title: string) => {
  return title
    .replace(/[<>:"/\\|?*]/g, '-')
    .replace(/_+/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

export interface DownloadVideoFromPortfolioOptions {
  download_urls?: Record<string, string>;
  download_url?: string;
  videoTitle?: string;
}

/**
 * Downloads a video from portfolio. Uses download_urls (first entry) if present, else download_url.
 * Call only when browser is available and user is allowed to download.
 */
export function downloadVideoFromPortfolio(
  downloadService: DownloadService,
  options: DownloadVideoFromPortfolioOptions
): void {
  const { download_urls, download_url, videoTitle } = options;
  const hasQualityUrls =
    download_urls &&
    typeof download_urls === 'object' &&
    Object.keys(download_urls).length > Application.ZERO;

  if (hasQualityUrls) {
    const entries = Object.entries(download_urls) as [string, string][];
    const first = entries[Application.ZERO];
    if (first) {
      const [label, url]: [string, string] = first;
      const ext = url.split('.').pop()?.split('?')[Application.ZERO] || Application.MP4;
      const baseName = videoTitle || `video-${Date.now()}`;
      const fileName = `${getSafeTitle(baseName)}_${label}.${ext}`;
      downloadService.downloadFileViaLink(url, fileName);
    }
    return;
  }
  if (download_url) {
    const ext = download_url.split('.').pop()?.split('?')[Application.ZERO] || Application.MP4;
    const baseName = videoTitle || `video-${Date.now()}`;
    const fileName = `${getSafeTitle(baseName)}.${ext}`;
    downloadService.downloadFileViaLink(download_url, fileName);
  }
}

export const formatTimeHrMinSec = (seconds: number) => {
  if (!seconds || isNaN(seconds)) return '0:00';
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > Application.ZERO) {
    return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Encodes a portfolio SK by replacing # with -
 * @param sk - The portfolio SK to encode
 * @returns The encoded SK
 */
export const encodePortfolioSk = (sk: string): string => {
  if (!sk) return '';
  return sk.replace(/#/g, '-');
};

/**
 * Generates share message and URL for a portfolio item
 * @param userProfile - User profile object with user_name or userName
 * @param sk - Portfolio SK to encode
 * @param portfolioType - Type of portfolio (music, video, podcast)
 * @param portfolioTypeId - Optional portfolio type ID for podcast and video
 * @returns Object with msg, url, and userName
 */
export const getSharePortfolioMsg = (
  userProfile: any,
  sk: string,
  portfolioType: string,
  portfolioTypeId?: string
): { msg: string; url: string; userName: string } => {
  const userName = userProfile?.user_name || userProfile?.userName;
  const encodedSk = encodePortfolioSk(sk);
  const baseUrl =
    environment.websiteURL?.replace(/\/$/, '') ||
    (typeof window !== 'undefined' ? window.location.origin : '');

  const routePath = PORTFOLIO_DETAIL_ROUTE_PATH_MAP[portfolioType];
  const shareMsg = PORTFOLIO_INDIVIDUAL_SHARE[portfolioType];
  const portfolioUrl = `${baseUrl}/dashboard/portfolio/${userName}/${routePath}/${encodedSk}?portfolioType=${portfolioType}&portfolioTypeId=${portfolioTypeId || ''}`;
  return {
    userName,
    msg: shareMsg,
    url: portfolioUrl
  };
};

/**
 * Generates share message and URL for a portfolio master item (album, series, collection).
 * @param userProfile - User profile object with user_name or userName
 * @param sk - Portfolio SK to encode
 * @param portfolioType - Type of portfolio (MUSIC, VIDEO, PODCAST, CUSTOM)
 * @param portfolioTypeId - Optional portfolio type ID for query param
 * @param shareUrlOverride - Optional pre-built URL to use instead of building one
 * @returns Object with msg, url, and userName
 */
export const getSharePortfolioMsgMaster = (
  userProfile: any,
  sk: string,
  portfolioType: string,
  portfolioTypeId?: string,
  shareUrlOverride?: string
): { msg: string; url: string; userName: string } => {
  const userName = userProfile?.user_name || userProfile?.userName;
  const encodedSk = encodePortfolioSk(sk);
  const baseUrl =
    environment.websiteURL?.replace(/\/$/, '') ||
    (typeof window !== 'undefined' ? window.location.origin : '');

  const routePath = PORTFOLIO_MASTER_ROUTE_PATH_MAP[portfolioType];
  const shareMsg = PORTFOLIO_MASTER_SHARE[portfolioType];
  const portfolioUrl =
    shareUrlOverride ||
    `${baseUrl}/dashboard/portfolio/${userName}/${routePath}/${encodedSk}?portfolioType=${portfolioType}&portfolioTypeId=${portfolioTypeId || ''}`;
  return {
    userName,
    msg: shareMsg,
    url: portfolioUrl
  };
};

export interface PortfolioShareModalInfoParams {
  userData: any;
  sk: string;
  portfolioMsg: string;
  url: string;
  userName: string;
  image?: string;
  isMyProfile: boolean;
}

/**
 * Builds the modal info object for opening the portfolio share dialog.
 * Use with getSharePortfolioMsg or getSharePortfolioMsgMaster to get msg, url, userName.
 */
export const getPortfolioShareModalInfo = (
  params: PortfolioShareModalInfoParams
): Record<string, unknown> => {
  const {
    userData,
    sk,
    portfolioMsg: msg,
    url,
    userName,
    image = '',
    isMyProfile
  } = params;
  return {
    isMyProfile,
    gsi4pk: userData?.gsi4pk,
    firstName: userData?.first_name,
    lastName: userData?.last_name,
    pk: userData?.pk,
    sk,
    sharingResource: SHARED_DIALOG_CONSTS.PORTFOLIO_SHARE,
    url,
    image,
    username: userName,
    otherUserEmail: userData?.email,
    portfolioMsg: msg
  };
};

/** Parent row from album / individual content APIs (`parent_details`). */
export interface ParentDetails {
  pk?: string;
  sk?: string;
  type?: string;
  title?: string;
}

export interface PortfolioAlbumBreadcrumbFields {
  portfolio_title?: string;
  parent_details?: ParentDetails;
}

export interface PortfolioIndividualBreadcrumbFields {
  portfolio_title?: string;
  portfolio_details?: ParentDetails;
  parent_details?: ParentDetails;
}

function portfolioBreadcrumbAlbumSeriesSegment(portfolioType: string): string {
  const upper = (portfolioType || '').toUpperCase();
  const map = PORTFOLIO_MASTER_ROUTE_PATH_MAP as Record<string, string>;
  const key =
    upper in PORTFOLIO_MASTER_ROUTE_PATH_MAP
      ? upper
      : PORTFOLIO_CONSTS.TYPE.MUSIC;
  return map[key] || map[PORTFOLIO_CONSTS.TYPE.MUSIC];
}

export function getPortfolioHubNavigationCommands(
  userId: string,
  isDashboardRoute: boolean
): (string | number)[] {
  return isDashboardRoute ? ['', userId] : ['/monetize/portfolio'];
}

export function getPortfolioBannerViewNavigationCommands(
  userId: string,
  portfolioTypeId: string,
  isDashboardRoute: boolean
): (string | number)[] {
  if (isDashboardRoute) {
    return ['/dashboard/portfolio', userId, 'banner-view', portfolioTypeId];
  }
  return ['/monetize/portfolio', 'banner-view', portfolioTypeId];
}

export function getAlbumSeriesNavigationCommands(
  userId: string,
  portfolioType: string,
  encodedSk: string,
  isDashboardRoute: boolean
): (string | number)[] {
  const segment = portfolioBreadcrumbAlbumSeriesSegment(portfolioType);
  if (isDashboardRoute) {
    return ['/dashboard/portfolio', userId, segment, encodedSk];
  }
  return ['/monetize/portfolio', segment, encodedSk];
}